import type { PerfilUsuario } from '../autenticacao/tipos.js';

/**
 * Matriz de perfis do painel (RF-078).
 *
 * Esta é a MESMA tabela que a tela de Usuários mostra ao supervisor e a mesma
 * que decide se um botão existe. Uma fonte só: se a regra mudar, a tela e a
 * decisão mudam juntas, e nunca uma sem a outra.
 *
 * A interface é a primeira das duas camadas. Ação que o perfil não permite
 * **não é renderizada** — nem desabilitada, nem escondida por CSS. A segunda
 * camada é a API, que recusa de qualquer forma: esconder botão é conforto,
 * não segurança.
 */

export const PERFIS: readonly PerfilUsuario[] = ['Administrador', 'Supervisor', 'Consulta'];

export type Acao =
  | 'ver'
  | 'usuarios.gerenciar'
  | 'operadores.gerenciar'
  | 'dispositivos.gerenciar'
  | 'depositos.importarDoErp'
  | 'inventarios.liberarReserva'
  | 'integracao.gerenciar'
  | 'sincronizacao.gerenciar'
  | 'operacoes.agir'
  | 'etiquetas.imprimir'
  | 'identificacao.ver'
  | 'leituraPorImagem.ver';

export type LinhaDaMatriz = {
  readonly acao: Acao;
  readonly rotulo: string;
  readonly perfis: readonly PerfilUsuario[];
};

export const LINHAS_DA_MATRIZ: readonly LinhaDaMatriz[] = [
  {
    acao: 'ver',
    rotulo: 'Ver todas as telas',
    perfis: ['Administrador', 'Supervisor', 'Consulta'],
  },
  {
    acao: 'usuarios.gerenciar',
    rotulo: 'Usuários do painel (cadastro, senha, perfil)',
    perfis: ['Administrador'],
  },
  {
    acao: 'operadores.gerenciar',
    rotulo: 'Operadores do app (cadastro, PIN)',
    perfis: ['Administrador', 'Supervisor'],
  },
  {
    acao: 'dispositivos.gerenciar',
    rotulo: 'Dispositivos (cadastrar, parear, bloquear)',
    perfis: ['Administrador'],
  },
  {
    // Só o administrador, como a integração: o botão fala com o ERP do cliente
    // e regrava a lista de depósitos que todos os coletores do armazém baixam.
    acao: 'depositos.importarDoErp',
    rotulo: 'Depósitos: sincronizar do ERP',
    perfis: ['Administrador'],
  },
  {
    // O supervisor entra porque é ele quem está no armazém quando um coletor
    // some no meio de uma contagem: esperar o administrador deixaria o
    // depósito travado para todo mundo até o dia seguinte.
    acao: 'inventarios.liberarReserva',
    rotulo: 'Inventários: liberar depósito reservado para contagem',
    perfis: ['Administrador', 'Supervisor'],
  },
  {
    // Só o administrador, e o supervisor fica de fora de propósito: trocar o
    // endereço, a empresa ou a sigla muda o DESTINO de tudo o que esta
    // plataforma envia ao sistema do cliente, e ligar a integração é o que
    // autoriza esse tráfego a existir. Não é a mesma natureza de decisão que
    // liberar um depósito travado.
    acao: 'integracao.gerenciar',
    rotulo: 'Integração com o ERP (conexão, credencial, ligar/desligar)',
    perfis: ['Administrador'],
  },
  {
    // Só o administrador, como na API: a carga inicial lê o ERP inteiro do
    // cliente, e o intervalo da periódica decide quantas vezes por dia o
    // armazém pergunta ao Senior. Os outros perfis acompanham o resultado
    // pelo resumo e pelas listas da tela de Sincronização.
    acao: 'sincronizacao.gerenciar',
    rotulo: 'Sincronização com o ERP (carga inicial, periódica)',
    perfis: ['Administrador'],
  },
  {
    acao: 'operacoes.agir',
    rotulo: 'Ações operacionais (divergência, fila, conflito)',
    perfis: ['Administrador', 'Supervisor'],
  },
  {
    // A mesma regra do `GET /produtos/qrcode` (qr-code.md §15.1): a etiqueta
    // é o que o coletor vai ler no inventário, e quem a cola é quem responde
    // pelo armazém.
    acao: 'etiquetas.imprimir',
    rotulo: 'Etiquetas de produto (gerar e imprimir o QR)',
    perfis: ['Administrador', 'Supervisor'],
  },
  {
    // A mesma regra do `GET /configuracoes/identificacao`. Mudar o modo é
    // `integracao.gerenciar` (só o Administrador): troca o que toda etiqueta
    // nova carrega e como todo ciclo novo de inventário conta.
    acao: 'identificacao.ver',
    rotulo: 'Identificação no armazém: ver se é por produto ou por lote',
    perfis: ['Administrador', 'Supervisor'],
  },
  {
    // A mesma regra do `GET /matching-qr/configuracao` (qr-code.md §6.1): o
    // Supervisor lê com que régua a contagem foi feita. Ligar e mudar os
    // limiares é `integracao.gerenciar` (só o Administrador), como o `PUT`.
    acao: 'leituraPorImagem.ver',
    rotulo: 'Leitura por imagem no inventário: ver se está ligada e com que parâmetros',
    perfis: ['Administrador', 'Supervisor'],
  },
];

export const ACOES: readonly Acao[] = LINHAS_DA_MATRIZ.map((linha) => linha.acao);

/**
 * O perfil da sessão pode a ação?
 *
 * Sem perfil — sessão ainda carregando, ou já encerrada — a resposta é não.
 * Falha fechado: enquanto não há prova de quem é, não há ação nenhuma.
 */
export function perfilPode(perfil: PerfilUsuario | null | undefined, acao: Acao): boolean {
  if (perfil === null || perfil === undefined) {
    return false;
  }

  const linha = LINHAS_DA_MATRIZ.find((candidata) => candidata.acao === acao);
  return linha?.perfis.includes(perfil) ?? false;
}

/** Rótulo em português da ação, para a matriz visível na tela de Usuários. */
export function rotuloDaAcao(acao: Acao): string {
  return LINHAS_DA_MATRIZ.find((linha) => linha.acao === acao)?.rotulo ?? acao;
}
